<template>
  <v-card>
    <div v-padding="3">
      <v-stepper-label
        v-if="isRenderWarningMessage"
        icon="warning"
        v-margin.bottom="2"
      >
        <v-row>
          <v-col xs="7">
            <v-text type="caption">
              Внимание!<br />
              Заполняйте поля в строгом соответствии с паспортом проверяемого.
              Перед тем, как нажать кнопку «Выполнить запрос», еще раз
              внимательно проверьте правильность введенной информации. Деньги за
              некорректный (содержащий ошибки) запрос не возвращаются.
            </v-text>
          </v-col>
        </v-row>
      </v-stepper-label>

      <v-form
        ref="form"
        :errors="errors"
        :defaultValues="defaultValues"
        :validate="(val) => onValidate(val, agreemenWay === 'crv', !isIdentificationRequired)"
        @submit="handleSubmit"
        :readonly="isRequestCreating"
      >
        <template scope="api">
          <v-stepper-content>
            <v-row>
              <v-col xs="6">
                <div v-if="isRenderOrgInnTitle" v-margin.top="1">
                  <v-text type="caption" v-margin.bottom="2">
                    Укажите ИНН и выберете подходящую компанию из предложенных
                    вариантов (или оставьте ИНН как есть, если ничего не
                    нашлось)
                  </v-text>
                </div>
                
                <v-form-input
                  v-if="fieldIsRender('ipInn')"
                  :disabled="needVerification"
                  v-margin.bottom="2"
                  field="ipInn"
                  type="number"
                  maxlength="12"
                  :required="fieldIsRequired('ipInn')"
                  label="ИНН"
                >
                    <template v-if="shouldShowCaption" slot="caption">
                      <v-text type="caption" >Заполните для получения данных о наличии самозапрета</v-text>
                    </template>
              </v-form-input>

                <v-form-input
                  v-if="fieldIsRender('orgInn')"
                  :disabled="needVerification"
                  v-margin.bottom="2"
                  field="orgInn"
                  maxlength="10"
                  :required="fieldIsRender('orgInn')"
                  label="ИНН"
                  type="number"
                  :items="dadataOrgs"
                  @input="onChangeOrgInn"
                  :getItemId="getOrgInnId"
                  :getItemCaption="getOrgInnItemCaption"
                />

                <!-- Personal -->

                <div v-margin.top="4" v-if="isRenderPersonTitle">
                  <v-text type="title" v-margin.bottom="1">
                    Руководитель компании
                  </v-text>
                </div>

                <v-form-input
                  v-if="fieldIsRender('regions')"
                  :disabled="needVerification"
                  :items=regions
                  v-margin.bottom="2"
                  field="regions"
                  :required="fieldIsRequired('regions')"
                  :multiple = true
                  label="Регионы"
                  caption="Можно выбрать максимум два региона или использовать поиск по всем регионам."
                  :maxSelectItems = 2
                  :altItem="{id: 0, name: 'Все регионы' }"
                />
                  <v-form-input
                  v-if="fieldIsRender('lastName')"
                  :disabled="needVerification"
                  v-margin.bottom="2"
                  field="lastName"
                  :required="fieldIsRequired('lastName')"
                  label="Фамилия"
                />
                <v-form-input
                  v-if="fieldIsRender('firstName')"
                  :disabled="needVerification"
                  v-margin.bottom="2"
                  field="firstName"
                  :required="fieldIsRequired('firstName')"
                  label="Имя"
                />
                <v-form-input
                  v-if="fieldIsRender('middleName')"
                  :disabled="needVerification"
                  v-margin.bottom="2"
                  field="middleName"
                  :required="fieldIsRequired('middleName')"
                  label="Отчество"
                />
                <v-form-input
                  v-if="fieldIsRender('dateBirth')"
                  :disabled="needVerification"
                  v-margin.bottom="2"
                  field="dateBirth"
                  :required="fieldIsRequired('dateBirth')"
                  label="Дата рождения"
                  caption="ДД / ММ / ГГГГ"
                  mask="date"
                />
                <v-form-input
                  v-if="fieldIsRender('placeBirth')"
                  :disabled="needVerification"
                  v-margin.bottom="2"
                  field="placeBirth"
                  :required="fieldIsRequired('placeBirth')"
                  label="Место рождения"
                  maxlength="200"
                />
                <v-form-input
                  v-if="fieldIsRender('position')"
                  :disabled="needVerification"
                  v-margin.bottom="2"
                  field="position"
                  :required="fieldIsRequired('position')"
                  label="Должность"
                />

                <!-- Passport -->

                <div v-margin.top="4" v-if="isRenderPassport">
                  <v-text type="title" v-margin.bottom="1"> Паспорт </v-text>
                </div>

                <v-form-input
                  v-if="fieldIsRender('documentSeries')"
                  :disabled="needVerification"
                  v-margin.bottom="2"
                  field="documentSeries"
                  type="number"
                  maxlength="4"
                  :required="fieldIsRequired('documentSeries')"
                  label="Паспорт серия"
                />
                <v-form-input
                  v-if="fieldIsRender('documentNumber')"
                  :disabled="needVerification"
                  v-margin.bottom="2"
                  field="documentNumber"
                  type="number"
                  maxlength="6"
                  :required="fieldIsRequired('documentNumber')"
                  label="Паспорт номер"
                />
                <v-form-input
                  v-if="fieldIsRender('issuingAuthority')"
                  :disabled="needVerification"
                  v-margin.bottom="2"
                  field="issuingAuthority"
                  :required="fieldIsRequired('issuingAuthority')"
                  label="Кем выдан"
                />
                <v-form-input
                  v-if="fieldIsRender('issuingDate')"
                  :disabled="needVerification"
                  v-margin.bottom="2"
                  field="issuingDate"
                  :required="fieldIsRequired('issuingDate')"
                  label="Когда выдан"
                  caption="ДД / ММ / ГГГГ"
                  mask="date"
                />

                <v-form-input
                  v-if="fieldIsRender('authorityCode')"
                  :disabled="needVerification"
                  v-margin.bottom="2"
                  field="authorityCode"
                  type="number"
                  :required="fieldIsRequired('authorityCode')"
                  label="Код подразделения"
                  mask="issuingAuthorityDepartment"
                />

                <!-- Skiptrace -->

                <v-form-input
                  v-if="fieldIsRender('actualityMonthLimitCount')"
                  field="actualityMonthLimitCount"
                  select
                  :items="[
                    { name: 'Без ограничения', value: null },
                    { name: 'Год', value: 12 },
                    { name: 'Полгода', value: 6 },
                    { name: '3 месяца', value: 3 },
                    { name: 'Месяц', value: 1 },
                  ]"
                  label="Границы поиска"
                  v-margin.bottom="2"
                  :required="fieldIsRequired('actualityMonthLimitCount')"
                />

                <v-form-checkbox
                  data=true
                  v-if="fieldIsRender('searchByRelatedPeople')"
                  :disabled="needVerification"
                  v-margin.bottom="2"
                  field="searchByRelatedPeople"
                  :required="fieldIsRequired('searchByRelatedPeople')"
                >
                  <template slot="label" scope="label">
                    <v-text> Искать по связанным лицам </v-text>
                  </template>
                </v-form-checkbox>

                <!-- Income -->

                <v-form-input
                  v-if="fieldIsRender('income')"
                  :disabled="needVerification"
                  v-margin.bottom="2"
                  field="income"
                  type="number"
                  maxlength="10"
                  :required="fieldIsRequired('income')"
                  label="Доход в месяц"
                >
                  <template slot="caption" scope="caption">
                    <v-font-icon grey>error_outline</v-font-icon>
                    <span v-margin.left="1">Для расчёта долговой нагрузки</span>
                  </template>
                </v-form-input>

                <!-- DriverLicense -->

                <div v-margin.top="4" v-if="isRenderDriverLicenseTitle">
                  <v-text type="title" v-margin.bottom="1">
                    Водительское удостоверение
                  </v-text>
                </div>

                <v-form-input
                  v-if="fieldIsRender('driverLicense')"
                  :disabled="needVerification"
                  v-margin.bottom="2"
                  field="driverLicense"
                  :required="fieldIsRequired('driverLicense')"
                  label="Серия и номер ВУ"
                  mask="driverLicense"
                />
                <v-form-input
                  v-if="fieldIsRender('driverLicenseIssued')"
                  :disabled="needVerification"
                  v-margin.bottom="2"
                  field="driverLicenseIssued"
                  :required="fieldIsRequired('driverLicenseIssued')"
                  label="Дата выдачи ВУ"
                  caption="ДД / ММ / ГГГГ"
                  mask="date"
                />

                <div v-margin.top="4" v-if="isRenderCarTransportTitle">
                  <v-text type="title" v-margin.bottom="1">
                    Транспортное средство
                  </v-text>
                </div>
                <v-form-input
                  v-if="fieldIsRender('vehicleLicensePlate')"
                  :disabled="needVerification"
                  v-margin.bottom="2"
                  field="vehicleLicensePlate"
                  :required="fieldIsRequired('vehicleLicensePlate')"
                  label="Государственный регистрационный знак"
                  mask="vehicleLicensePlate"
                />
                <v-form-input
                  v-if="fieldIsRender('vehicleRegistrationCertificate')"
                  :disabled="needVerification"
                  v-margin.bottom="2"
                  field="vehicleRegistrationCertificate"
                  :required="fieldIsRequired('vehicleRegistrationCertificate')"
                  label="СТС (свидетельство о регистрации ТС)"
                  mask="vehicleRegistrationCertificate"
                />
                <v-form-input
                  v-if="fieldIsRender('vin')"
                  :disabled="needVerification"
                  v-margin.bottom="2"
                  field="vin"
                  :required="fieldIsRequired('vin')"
                  label="VIN транспортного средства"
                  mask="vin"
                />

                <!-- Address -->

                <div v-margin.top="4" v-if="isRenderPropTitle">
                  <v-text type="title" v-margin.bottom="1">
                    {{ isIndividual ? "Адрес прописки" : "Юридический адрес" }}
                  </v-text>
                </div>

                <v-form-input
                  v-if="fieldIsRender('propIndex')"
                  :disabled="needVerification"
                  v-margin.bottom="2"
                  field="propIndex"
                  type="number"
                  maxlength="6"
                  :required="fieldIsRequired('propIndex')"
                  label="Индекс"
                />
                <v-form-input
                  v-if="fieldIsRender('propRegion')"
                  :disabled="needVerification"
                  v-margin.bottom="2"
                  field="propRegion"
                  :items="regions"
                  :required="fieldIsRequired('propRegion')"
                  :label="
                    isIndividual ? 'Область регистрации' : 'Область (регион)'
                  "
                />
                <v-form-input
                  v-if="fieldIsRender('propCity')"
                  :disabled="needVerification"
                  v-margin.bottom="2"
                  field="propCity"
                  :required="fieldIsRequired('propCity')"
                  label="Город"
                />
                <v-form-input
                  v-if="fieldIsRender('propStreet')"
                  :disabled="needVerification"
                  v-margin.bottom="2"
                  field="propStreet"
                  :required="fieldIsRequired('propStreet')"
                  label="Улица"
                />
                <v-row>
                  <v-col>
                    <v-form-input
                      v-if="fieldIsRender('propHouse')"
                      :disabled="needVerification"
                      v-margin.bottom="2"
                      field="propHouse"
                      type="number"
                      maxlength="4"
                      :required="fieldIsRequired('propHouse')"
                      label="Дом"
                    />
                  </v-col>
                  <v-col>
                    <v-form-input
                      v-if="fieldIsRender('propAppartment')"
                      :disabled="needVerification"
                      v-margin.bottom="2"
                      field="propAppartment"
                      type="number"
                      maxlength="4"
                      :required="fieldIsRequired('propAppartment')"
                      :label="isIndividual ? 'Квартира' : 'Офис'"
                    />
                  </v-col>
                  <v-col>
                    <v-form-input
                      v-if="fieldIsRender('propCorp')"
                      :disabled="needVerification"
                      v-margin.bottom="2"
                      field="propCorp"
                      maxlength="4"
                      :required="fieldIsRequired('propCorp')"
                      label="Корпус"
                    />
                  </v-col>
                </v-row>

                <v-form-checkbox
                  v-if="fieldIsRender('sameAddress')"
                  :disabled="needVerification"
                  v-margin.bottom="2"
                  field="sameAddress"
                  :required="fieldIsRequired('sameAddress')"
                >
                  <template slot="label" scope="label">
                    <v-text>
                      Фактический и юридический адрес регистрации совпадают
                    </v-text>
                  </template>
                </v-form-checkbox>

                <div v-if="!api.getValue('sameAddress').length">
                  <div
                    v-if="isRenderFactTitle"
                    v-margin.bottom="1"
                    v-margin.top="4"
                  >
                    <v-text type="title">Фактический адрес</v-text>
                  </div>

                  <v-form-input
                    v-if="fieldIsRender('factIndex')"
                    :disabled="needVerification"
                    v-margin.bottom="2"
                    field="factIndex"
                    type="number"
                    maxlength="6"
                    :required="fieldIsRequired('factIndex')"
                    label="Индекс"
                  />
                  <v-form-input
                    v-if="fieldIsRender('factRegion')"
                    :disabled="needVerification"
                    v-margin.bottom="2"
                    field="factRegion"
                    :items="regions"
                    :required="fieldIsRequired('factRegion')"
                    :label="
                      isIndividual ? 'Область проживания' : 'Область (регион)'
                    "
                  />
                  <v-form-input
                    v-if="fieldIsRender('factCity')"
                    :disabled="needVerification"
                    v-margin.bottom="2"
                    field="factCity"
                    :required="fieldIsRequired('factCity')"
                    label="Город"
                  />
                  <v-form-input
                    v-if="fieldIsRender('factStreet')"
                    :disabled="needVerification"
                    v-margin.bottom="2"
                    field="factStreet"
                    :required="fieldIsRequired('factStreet')"
                    label="Улица"
                  />
                  <v-row>
                    <v-col>
                      <v-form-input
                        v-if="fieldIsRender('factHouse')"
                        :disabled="needVerification"
                        v-margin.bottom="2"
                        field="factHouse"
                        type="number"
                        maxlength="4"
                        :required="fieldIsRequired('factHouse')"
                        label="Дом"
                      />
                    </v-col>
                    <v-col>
                      <v-form-input
                        v-if="fieldIsRender('factAppartment')"
                        :disabled="needVerification"
                        v-margin.bottom="2"
                        field="factAppartment"
                        type="number"
                        maxlength="4"
                        :required="fieldIsRequired('factAppartment')"
                        :label="isIndividual ? 'Квартира' : 'Офис'"
                      />
                    </v-col>
                    <v-col>
                      <v-form-input
                        v-if="fieldIsRender('factCorp')"
                        :disabled="needVerification"
                        v-margin.bottom="2"
                        field="factCorp"
                        maxlength="4"
                        :required="fieldIsRequired('factCorp')"
                        label="Корпус"
                      />
                    </v-col>
                  </v-row>
                </div>

                <v-form-input
                  v-if="fieldIsRender('orgName')"
                  :disabled="needVerification"
                  v-margin.bottom="2"
                  field="orgName"
                  :required="fieldIsRequired('orgName')"
                  label="Название организации"
                />

                <v-form-input
                  v-if="fieldIsRender('orgOgrn')"
                  :disabled="needVerification"
                  v-margin.bottom="2"
                  field="orgOgrn"
                  type="number"
                  maxlength="13"
                  :required="fieldIsRequired('orgOgrn')"
                  label="ОГРН"
                />

                <v-form-input
                  v-if="fieldIsRender('okato')"
                  :disabled="needVerification"
                  v-margin.bottom="2"
                  field="okato"
                  type="number"
                  maxlength="11"
                  :required="fieldIsRequired('okato')"
                  label="ОКАТО"
                />

                <v-form-input
                  v-if="fieldIsRender('fias')"
                  :disabled="needVerification"
                  v-margin.bottom="2"
                  field="fias"
                  v-show="false"
                  :required="fieldIsRequired('fias')"
                  label="ФИАС"
                />

                <v-form-input
                  v-if="fieldIsRender('innOrOgrn')"
                  :disabled="needVerification"
                  v-margin.bottom="2"
                  field="innOrOgrn"
                  type="number"
                  maxlength="13"
                  :required="fieldIsRender('innOrOgrn')"
                  label="ИНН или ОГРН"
                />

                <div
                  :class="theme.checkBtn"
                  v-if="productData.identificationRequired"
                >
                  <div v-margin.bottom="3">
                    <v-text type="subheadling">Заявление-согласие </v-text>
                  </div>                  
                  <div>










                    <v-text
                      >Запрос основной части кредитной истории в бюро кредитных
                      историй может осуществляться только с согласия субъекта
                      кредитной истории</v-text
                    >
                  </div>
                  <!-- Documents -->
                  <v-row
                    v-margin.top="3"
                    v-if="
                      productData.availableAgreementTypes.length > 1 &&
                      needVerification
                    "
                  >
                    <v-col xs="7">
                      <v-form-radio
                        v-margin.bottom="2"
                        field="agreemenWay"
                        :data="'crv'"
                        label="Электронное согласие"
                        :clickEvent="changeAgreemenWay"
                    /></v-col>
                    <v-col xs="5">
                      <v-form-radio
                        v-margin.bottom="2"
                        field="agreemenWay"
                        :data="'paper'"
                        label="Скан-копия"
                        :clickEvent="changeAgreemenWay" /></v-col
                  ></v-row>
                  <v-button
                    v-if="agreemenWay === null"
                    type="button"
                    primary
                    :disabled="
                      checkHasCrvLoading ||
                      api.isInvalid ||
                      isRequestCreating ||
                      isInvalidIdentification
                    "
                    @click="() => checkHasCrv(api.getValues())"
                  >
                    Проверить наличие согласия.
                  </v-button>
                </div>

                <div
                  v-if="
                    agreemenWay === 'crv' && needVerification && !isPhoneCorrect
                  "
                  v-padding.top="3"
                  v-padding.bottom="3"
                >
                  <div v-margin.bottom="2">
                    <v-text>
                      По СМС будет отправлена инструкция для прохождения удаленной идентификации
                    </v-text>
                  </div>

                  <v-form-input
                    field="phone"
                    mask="phone"
                    label="Контактный номер проверяемого лица"
                    :required="true"
                    v-margin.bottom="2"
                  >
                    <!-- <template slot="caption" scope="props">
                      <span v-if="isPhoneCorrect === false">
                        Телефон не найден. Пожалуйста попробуйте другой
                        телефон.</span
                      >
                      <span v-if="timerId">
                        Отправить повторно можно через {{ remainingTime }}
                      </span>
                    </template> -->
                    <template slot="caption" scope="props">
                      <span v-if="isPhoneCorrect === false">
                        Функционал временно недоступен, для выполнения запроса
                        просим приложить бумажное согласие.
                      </span>
                    </template>
                  </v-form-input>
                  <div v-if="agreemenWay === 'crv' && needVerification">
                    <div v-if="isPhoneCorrect">
                      <v-text>
                        Телефон найден. Нажмите "Запросить согласие" и мы
                        выполним запрос сразу, как субъект даст согласие на
                        получение данных из Кредитного Бюро.
                      </v-text>
                    </div>
                  </div>
                  <v-button
                    type="button"
                    primary
                    :disabled="
                      checkHasPhoneLoading ||
                      isInvalidIdentification ||
                      api.isInvalid ||
                      // ссаный костыль, потому что я не знаю, как в vue с динамической валидацией дружит
                      !api.getValue('phone') ||
                      api.getValue('phone').length !== 18 ||
                      Boolean(timerId)
                    "
                    @click="() => checkPhoneCompatibility(api.getValues())"
                  >
                    Проверить возможность
                  </v-button>
                </div>

                <div
                  v-if="agreemenWay === 'paper' && needVerification"
                  v-padding.top="3"
                  v-padding.bottom="3"
                >




                  <div v-margin.bottom="2">
                    <v-text v-if="api.isInvalid" error>
                      Чтобы скачать сформированное заявление-согласие,
                      необходимо заполнить обязательные поля
                    </v-text>
                    <v-text v-else>
                      Скачайте уже заполненное заявление-согласие в удобном
                      формате
                    </v-text>
                  </div>
                  <div>
                    <v-text v-margin.right="3">
                      <v-font-icon grey :disabled="api.isInvalid"
                        >file_download</v-font-icon
                      >
                      <v-link
                        :disabled="api.isInvalid"
                        to="javascript:;"
                        @click="downloadContract('doc', api.getValues())"
                      >
                        DOC
                      </v-link>
                    </v-text>

                    <v-text v-margin.right="3">
                      <v-font-icon grey :disabled="api.isInvalid"
                        >file_download</v-font-icon
                      >
                      <v-link
                        :disabled="api.isInvalid"
                        to="javascript:;"
                        @click="downloadContract('html', api.getValues())"
                      >
                        HTML
                      </v-link>
                    </v-text>

                    <v-text>
                      <v-font-icon grey :disabled="api.isInvalid"
                        >file_download</v-font-icon
                      >
                      <v-link
                        :disabled="api.isInvalid"
                        to="javascript:;"
                        @click="downloadContract('pdf', api.getValues())"
                      >
                        PDF
                      </v-link>
                    </v-text>
                  </div>

                  <v-form-file
                    v-if="fieldIsRender('contractScan')"
                    v-margin.bottom="2"
                    @upload="handleUploadScan('contractScan', $event)"
                    @remove="handleRemoveScan('contractScan', $event)"
                    :loader="loaders.contractScan"
                    :files="scans.contractScan"
                    field="contractScan"
                    label="Прикрепите скан заявления *"
                    caption="Прикрепите заявление-согласие, заполненное и подписанное от руки.
                                        Файлы: pdf, jpg, jpeg, png, tiff, tif 14Мб"
                  />

                  <v-form-file
                    v-if="fieldIsRender('passportScan')"
                    v-margin.bottom="2"
                    @upload="handleUploadScan('passportScan', $event)"
                    @remove="handleRemoveScan('passportScan', $event)"
                    :loader="loaders.passportScan"
                    :files="scans.passportScan"
                    field="passportScan"
                    label="Прикрепите скан паспорта *"
                    caption="Прикрепите разворот с фотографией и прописку.
                                        Файлы: pdf, jpg, jpeg, png, gif, tiff, tif 14Мб"
                  />

                  <v-form-file
                    v-if="fieldIsRender('ogrnScan')"
                    v-margin.bottom="2"
                    @upload="handleUploadScan('ogrnScan', $event)"
                    @remove="handleRemoveScan('ogrnScan', $event)"
                    :loader="loaders.ogrnScan"
                    :files="scans.ogrnScan"
                    field="ogrnScan"
                    label="Прикрепите ОГРН *"
                    caption="Загрузите скан ОГРН.
                                        Файлы: pdf, jpg, jpeg, png, gif, tiff, tif 14Мб"
                  />

                  <v-form-file
                    v-if="fieldIsRender('attorneyScan')"
                    v-margin.bottom="2"
                    @upload="handleUploadScan('attorneyScan', $event)"
                    @remove="handleRemoveScan('attorneyScan', $event)"
                    :loader="loaders.attorneyScan"
                    :files="scans.attorneyScan"
                    field="attorneyScan"
                    label="Прикрепите скан доверенности *"
                    caption="Загрузите скан доверенности, если заявление-согласие подписал не директор.
                                        Файлы: pdf, jpg, jpeg, png, gif, tiff, tif 14Мб"
                  />
                </div>
                <v-form-input 
                  v-if="fieldIsRender('monitoringEndDate')"
                  :disabled="needVerification"
                  v-margin.bottom="2"
                  field="monitoringEndDate"
                  :required="fieldIsRequired('monitoringEndDate')"
                  label="Дата окончания мониторинга"
                  caption="ММ/ГГГГ"
                  mask="dateWithoutDay"
                  placeholder="ММ/ГГГГ"
                  @input="onMonitoringDateChange"
                />

                <div v-if="fieldIsRender('monitoringEndDate') && monitoringDateError" v-margin.bottom="2"  class="error-message">
                  <v-text error type="caption" style="color: #ff4444;">
                    {{ monitoringDateError }}
                  </v-text>
                </div>

                <div v-if="fieldIsRender('monitoringEndDate')" v-margin.bottom="2">
                  <v-text type="caption">
                    Мониторинг действует по конец выбранного месяца включительно.
                  </v-text>
                </div>

                <div v-if="!isIdentificationRequired">
                  <v-text type="caption">
                    <v-link to="https://exbico.ru/page97982866.html" target="_blank">
                      Поручение на обработку ПД
                      <v-font-icon small inherit>
                        description
                      </v-font-icon>
                    </v-link>
                    <v-link to="https://exbico.ru/politika_konfdencialnosti" target="_blank">
                      Политика обработки ПД
                      <v-font-icon small inherit>
                        description
                      </v-font-icon>
                    </v-link>
                  </v-text>
                  <v-form-checkbox 
                    data=true
                    v-margin.bottom="2" 
                    field="agreementReceived"
                    :required=true>
                    <template slot="label" scope="label">
                      <v-text> Получено согласие субъекта на обработку его перс. данных. </v-text>
                    </template>
                  </v-form-checkbox>
                </div>
              </v-col>
            </v-row>
          </v-stepper-content>

          <v-stepper-label icon="error">
            <v-text type="subheadling"> Итого: {{ calculatedTotal }} руб </v-text>
          </v-stepper-label>
          <div>
            <v-stepper-content
              v-if="
                !productData.identificationRequired ||
                agreementId !== null ||
                (needVerification &&
                  (isPhoneCorrect || agreemenWay === 'paper'))
              "
            >
              <v-spinner :active="isRequestCreating">
                <v-button
                  type="submit"
                  primary
                  :disabled="
                    api.isInvalid ||
                    !isFormValid ||
                    isRequestCreating ||
                    isInvalidIdentification
                  "
                >
                  Запросить отчет
                </v-button>
              </v-spinner>
            </v-stepper-content>
          </div>
          <v-stepper-label icon="warning" :danger="true" v-if="isBackendError">
            <v-text type="caption" :danger="true"> {{backendErrorMessage}} </v-text>
          </v-stepper-label>
        </template>
      </v-form>
    </div>
  </v-card>
</template>

<script>
import { post, get } from "../../../services/api/api";
import throttle from "lodash.throttle";
import theme from "./theme.scss";
import {
  checkSubjectAgreemen,
  checkSubjectCrvCompatibility,
  log
} from "../../../services/api/crv";
function normalizeValues(values) {
  ["propRegion", "factRegion"].map((field) => {
    values[field] = values[field] && values[field].id;
  });

  if (values.hasOwnProperty("sameAddress")) {
    values.sameAddress = !!values.sameAddress.length;
  }

  ["contractScan", "passportScan", "ogrnScan", "attorneyScan"].map((field) => {
    delete values[field];
  });

  return values;
}

const checkResponseMap = {
  0: "withoutAgreement ",
  1: "paper",
  2: "crv",
};

function convertAgreementType(agreementName) {
  switch (agreementName) {
    case "paper":
      return 1;
    case "crv":
      return 2;
    case "withoutAgreement":
    default:
      return 0;
  }
}

export default {
  name: "request-profile",

  props: {
    onCreate: Function,
    onUpload: Function,
    onValidate: Function,
    isIndividual: Boolean,
    isIdentificationRequired: Boolean,
    productId: Number,
    total: {
      type: Number,
      default: 0,
    },
    theme: {
      type: Object,
      default() {
        return theme;
      },
    },
    productData: {
      id: Number,
      name: String,
      cost: Number,
      freeCount: Number,
      availableCount: Number,
      isIndividual: Boolean,
      isEnable: Boolean,
      crashingMessage: null,
      identificationRequired: Boolean,
      availableAgreementTypes: Array,
    },
    regions: {
      type: Array,
      default() {
        return [];
      },
    },
    fields: {
      type: Object,
      default() {
        return {};
      },
    },
  },

  data() {
    return {
      defaultValues: {
        sameAddress: [false],
        searchByRelatedPeople: [],
        actualityMonthLimitCount: { name: 'Год', value: 12 },
        regions: []
      },
      checkHasCrvLoading: false,
      checkHasPhoneLoading: false,
      agreemenWay: null,
      isPhoneCorrect: null,
      agreementId: null,
      needVerification: false,
      remainingTime: 30,
      timerId: null,
      scans: {
        contractScan: [],
        passportScan: [],
        ogrnScan: [],
        attorneyScan: [],
      },
      loaders: {
        contractScan: false,
        passportScan: false,
        ogrnScan: false,
        attorneyScan: false,
      },
      errors: {},
      isRequestCreating: false,
      isBackendError: false,
      backendErrorMessage: '',
      dadataOrgs: [],
      monitoringEndDate: null,
      monitoringDateError: null,
      calculatedTotal: this.productData.cost || 0,
    };
  },

  methods: {
    getOrgInnItemCaption(item) {
      return item.value;
    },

    getOrgInnId(item) {
      return item.data.inn;
    },

    onChangeOrgInn: throttle(function (value) {
      function sync(s) {
        const form = this.$refs.form;
        form.setValue("orgInn", s.data.inn);

        // Before filling of fields, clean up them
        form.setValue("firstName", "");
        form.setValue("middleName", "");
        form.setValue("lastName", "");
        form.setValue("position", "");
        form.setValue("orgOgrn", "");
        form.setValue("orgName", "");
        form.setValue("propIndex", "");
        form.setValue("propRegion", "");
        form.setValue("propCity", "");
        form.setValue("propStreet", "");
        form.setValue("propHouse", "");
        form.setValue("propAppartment", "");
        form.setValue("propCorp", "");
        form.setValue("okato", "");
        form.setValue("fias", "");

        if (s.data.management) {
          const { name, post } = s.data.management;
          if (name) {
            const [last, first, middle] = name.split(" ");
            form.setValue("firstName", first);
            form.setValue("middleName", middle);
            form.setValue("lastName", last);
          }
          form.setValue("position", post);
        }
        form.setValue("orgOgrn", s.data.ogrn);
        form.setValue("orgName", s.value);
        form.setValue("okato", s.data.okato);
        form.setValue("fias", s.data.address.data.fias_id);

        if (s.data.address && s.data.address.data) {
          const {
            postal_code,
            city,
            street,
            house,
            flat,
            block,
            region_type,
            region,
          } = s.data.address.data;

          form.setValue("propIndex", postal_code || "");
          form.setValue("propCity", city || "");
          form.setValue("propStreet", street || "");
          form.setValue("propHouse", house || "");
          form.setValue("propAppartment", flat || "");
          form.setValue("propCorp", block || "");

          if (region_type && region) {
            const regionsKeys = Object.keys(this.regions);
            for (let i = 0; regionsKeys.length; i++) {
              if (
                this.regions[i].name.toLowerCase() ===
                `${region} ${region_type}`.toLowerCase()
              ) {
                form.setValue("propRegion", this.regions[i]);
                break;
              }
            }
          }
        }
      }

      // This is very dirty hack because the Autosuggest has strange API.
      if (Number.isNaN(Number(value)) && this.dadataOrgs.length) {
        for (let i = 0; i < this.dadataOrgs.length; i++) {
          const o = this.dadataOrgs[i];
          if (o.value === value) {
            sync.call(this, o);
            // Set touched for enable visibility of validation errors
            [
              "firstName",
              "lastName",
              "middleName",
              "position",
              "propRegion",
              "propCity",
              "propStreet",
              "propHouse",
              "factRegion",
              "factCity",
              "factStreet",
              "factHouse",
              "orgName",
              "orgOgrn",
              "okato",
              "fias",
            ].forEach((x) => {
              this.$refs.form.setTouched(x, true);
            });
            this.dadataOrgs = [];
            return;
          }
        }
      }

      const dadataURL =
        "https://suggestions.dadata.ru/suggestions/api/4_1/rs/suggest/party";
      post(
        dadataURL,
        { query: value },
        {
          prefix: "",
          headers: { Authorization: `Token ${process.env.DADATA_TOKEN}` },
        }
      ).then((res) => {
        this.dadataOrgs = res.data.suggestions.filter(
          (x) => x.data.inn && x.data.inn.length === 10
        );
      });
    }, 500),

    downloadContract(type, values) {
      this.$emit("download-contract", {
        type,
        values: normalizeValues(values),
      });
    },

    fieldIsRender(field) {
      return this.fields.hasOwnProperty(field);
    },

    changeAgreemenWay(id) {
      if (id === "crv") {
        log({})
      }
      this.agreemenWay = id;
    },

    fieldIsRequired(field) {
      return this.fieldIsRender(field) && this.fields[field].required;
    },

    startTimer() {
      this.remainingTime = 30;
      // const timerId = setInterval(() => {
      //   if (this.remainingTime > 0) {
      //     this.remainingTime -= 1;
      //   } else {
      //     clearInterval(timerId);
      //     this.timerId = null;
      //   }
      // }, 1000);
      console.log("start Timer");
      this.timerId = true;
    },

    checkPhoneCompatibility(values) {
      this.checkHasPhoneLoading = true;
      return checkSubjectCrvCompatibility({
        fisrtName: values.firstName,
        middleName: values.middleName,
        lastName: values.lastName,
        documentSeries: values.documentSeries,
        documentNumber: values.documentNumber,
        phone: values.phone.replace(/\D/g, "").substring(1),
        issueDate: values.issuingDate,
        birthDate: values.dateBirth,
      })
        .then((r) => {
          this.checkHasPhoneLoading = false;
          this.isPhoneCorrect = r.data;
          if (!r.data) {
            this.startTimer();
          }
        })
        .catch((e) => {
          this.checkHasPhoneLoading = false;
          this.isPhoneCorrect = false;
          this.startTimer();
        });
    },

    checkHasCrv(values) {
      this.checkHasCrvLoading = true;
      checkSubjectAgreemen(normalizeValues(values)).then((r) => {
        if (r.data) {
          this.checkHasCrvLoading = false;
          this.agreementId = r.data;
        } else {
          this.needVerification = true;
          this.checkHasCrvLoading = false;

          const { productData, isIdentificationRequired } = this;
          if (isIdentificationRequired) {
            if (productData.availableAgreementTypes.length === 2) {
              this.agreemenWay = null;
              return;
            }
            if (productData.availableAgreementTypes[0]) {
              this.agreemenWay =
                checkResponseMap[productData.availableAgreementTypes[0]];
            }
          }
          this.errors = {
            ...this.errors,
            phone: "Заполните поле",
          };
        }
      });
    },

    handleSubmit(values) {
      if (this.isRequestCreating || this.isInvalidIdentification) {
        return;
      }

      if (this.isMonitoringProduct) {
        const dateError = this.validateMonitoringDate(values.monitoringEndDate);
        if (dateError) {
          this.monitoringDateError = dateError;
          this.isRequestCreating = false;
          
          this.$nextTick(() => {
            const errorElement = this.$el.querySelector('.error-message');
            if (errorElement) {
              errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          });
          
          return;
        }
      }

      this.isRequestCreating = true;
      this.isBackendError = false;
      this.monitoringDateError = null; 

      const dto = {
        ...values,
        searchByRelatedPeople: values.searchByRelatedPeople ? values.searchByRelatedPeople[0] === "true" : null,
        actualityMonthLimitCount:
          values !== null && values.actualityMonthLimitCount !== null
            ? values.actualityMonthLimitCount.value
            : null,
      };

      this.onCreate({
        fields: normalizeValues(dto),
        documents: this.uploadedDocuments,
        agreementType: convertAgreementType(this.agreemenWay),
        agreementId: this.agreementId,
      }).then((res) => {
        this.$router.push({ name: "requests" });
      }).catch(error => {
        this.backendErrorMessage = error.data.message;
        this.isRequestCreating = false;
        this.isBackendError = true;
      });
    },

    handleUploadScan(field, files) {
      this.loaders[field] = true;
      this.onUpload(field, files)
        .then((res) => {
          this.loaders[field] = false;

          const failedFiles = res.data
            .filter((file) => !file.success)
            .reduce((err, file) => {
              err[file.name] = file.message;
              return err;
            }, {});

          const uploadedFiles = res.data.filter((file) => file.success);

          if (Object.keys(failedFiles).length > 0) {
            this.errors = {
              ...this.errors,
              [field]: failedFiles,
            };
          }

          this.scans[field] = this.scans[field].concat(uploadedFiles);
        })
        .catch((res) => {
          this.loaders[field] = false;

          if (res.status === 400) {
            this.errors = {
              ...this.errors,
              [field]: res.data.message,
            };
          }
        });
    },

    handleRemoveScan(field, { file, index }) {
      const newFiles = this.scans[field].concat([]);
      newFiles.splice(index, 1);

      this.scans[field] = newFiles;
    },

    test(api) {
      window.api = api;
      console.log(api.getValues());
    },

    validateMonitoringDate(date) {
      
      if (!date) {
        return 'Поле обязательно для заполнения';
      }
      
      const dateRegex = /^(0[1-9]|1[0-2])\/\d{4}$/;
      if (!dateRegex.test(date)) {
        return 'Неверный формат даты. Используйте ММ/ГГГГ';
      }
      
      const [inputMonth, inputYear] = date.split('/').map(Number);
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1;
      
      if (inputMonth < 1 || inputMonth > 12) {
        return 'Месяц должен быть от 01 до 12';
      }
      
      if (inputYear < currentYear) {
        return 'Дата окончания мониторинга не может быть в прошлом';
      }
      
      if (inputYear === currentYear && inputMonth < currentMonth) {
        return 'Дата окончания мониторинга не может быть в прошлом';
      }
      
      return null;
    },      
    
    onMonitoringDateChange(value) {

      this.monitoringEndDate = value;
      this.monitoringDateError = this.validateMonitoringDate(value);
   
      this.updateTotal();
    },

    updateTotal() {
      const baseCost = this.productData.cost || 0;
      
      if (!this.monitoringEndDate) {
        this.calculatedTotal = baseCost;
        console.log('Стоимость без мониторинга:', this.calculatedTotal);
        return;
      }

      const dateError = this.validateMonitoringDate(this.monitoringEndDate);
      if (dateError) {
        this.calculatedTotal = baseCost;
        console.log('Дата невалидна, используем базовую стоимость');
        return;
      }

      const monthsCount = this.getMonthsCount(this.monitoringEndDate);
      this.calculatedTotal = baseCost * monthsCount;
    },

    getMonthsCount(monitoringDate) {
      if (!monitoringDate) return 1;
      
      try {
        const parts = monitoringDate.split('/');
        if (parts.length !== 2) {
          console.log('Неверный формат даты');
          return 1;
        }
        
        const inputMonth = parseInt(parts[0], 10);
        const inputYear = parseInt(parts[1], 10);
        
        if (isNaN(inputMonth) || isNaN(inputYear)) {
          console.log('Невалидные числа в дате');
          return 1;
        }
        
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth() + 1;
        
        const months = (inputYear - currentYear) * 12 + (inputMonth - currentMonth) + 1;
        const result = Math.max(1, months);
        
        return result;
      } catch (error) {
        return 1;
      }
    },
  },

  computed: {
    uploadedDocuments() {
      const { contractScan, passportScan, ogrnScan, attorneyScan } = this.scans;
      const documents = contractScan
        .map((file) => file.id)
        .concat(passportScan.map((file) => file.id))
        .concat(ogrnScan.map((file) => file.id))
        .concat(attorneyScan.map((file) => file.id));

      return documents;
    },

    isInvalidIdentification() {
      return false;
      return this.isIdentificationRequired
        ? ["contractScan", "passportScan", "ogrnScan", "attorneyScan"].some(
            (field) =>
              this.fieldIsRender(field) && this.fieldIsRequired(field)
                ? !this.scans[field].length
                : false
          )
        : false;
    },

    isRenderWarningMessage() {
      return ["documentSeries", "documentNumber"].some((field) =>
        this.fields.hasOwnProperty(field)
      );
    },

    isRenderPropTitle() {
      return ["propRegion", "propCity", "propStreet", "propHouse"].every(
        (field) => this.fields.hasOwnProperty(field)
      );
    },

    isRenderFactTitle() {
      return ["factRegion", "factCity", "factStreet", "factHouse"].every(
        (field) => this.fields.hasOwnProperty(field)
      );
    },

    isRenderScanTitle() {
      console.log(this.fields);
      return ["contractScan", "passportScan", "ogrnScan", "attorneyScan"].some(
        (field) => this.fields.hasOwnProperty(field)
      );
    },

    isRenderPersonTitle() {
      return (
        ["firstName", "middleName", "lastName", "position"].some((field) =>
          this.fields.hasOwnProperty(field)
        ) && !this.isIndividual
      );
    },

    isRenderPassport() {
      return [
        "documentSeries",
        "documentNumber",
        "driverLicense",
        "driverLicenseIssued",
        "vehicleLicensePlate",
        "vehicleRegistrationCertificate",
        "vin",
      ].every((field) => this.fields.hasOwnProperty(field));
    },

    isRenderDriverLicenseTitle() {
      return ["driverLicense", "driverLicenseIssued"].some((field) =>
        this.fields.hasOwnProperty(field)
      );
    },

    isRenderCarTransportTitle() {
      return [
        "vehicleLicensePlate",
        "vehicleRegistrationCertificate",
        "vin",
      ].some((field) => this.fields.hasOwnProperty(field));
    },

    isRenderOrgInnTitle() {
      return this.fields.hasOwnProperty("orgInn") && !this.isIndividual;
    },

    isFormValid() {
      const baseValid = !this.isInvalidIdentification;

      const dateValid = this.isMonitoringProduct 
      ? !this.monitoringDateError 
      : true;
      
      return baseValid && dateValid;
    },

    isMonitoringProduct() {
      return this.fieldIsRender('monitoringEndDate');
    },

    shouldShowCaption() {
      const captionProducts = [
        'Комплексный отчёт',
        'ККО',
        'ОКБ',
        'Скоринг Бюро',
        'НБКИ',
        'Мониторинг'
      ];
      
      return this.productData && this.productData.name 
        ? captionProducts.includes(this.productData.name)
        : false;
    }
  },

  mounted() {
    console.log(this.$props.productData);
    this.updateTotal();
    
    if (this.monitoringEndDate) {
      this.monitoringDateError = this.validateMonitoringDate(this.monitoringEndDate);
    }
  },
};
</script>



// WEBPACK FOOTER //
// RequestProfile.vue?26df75a8